import { Button, Masthead, MastheadBrand, MastheadContent, MastheadMain, MastheadToggle, PageToggleButton } from '@patternfly/react-core';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import './masthead.css';

import ConfigurationTargetSelector from './components/client-selector';
import { DEFAULT_APP_NAME } from './environments';
import routes from './routes';
import logo32w from './img/mvision-logo-white-32.png';
import { getCurrentLangKey } from './i18n';
import { appConfigSelectors } from './store/appConfig/appConfigSlice';
import { DeploymentMode } from './store/appConfig/appDeploymentInfoTypes';
import { configurationTargetSelectors } from './store/configurationTarget/configurationTargetSlice';
import { ProfileButton } from './components/ProfileButton';
import { HelpIcon, LeftPanelClose, RightPanelClose, TuneIcon } from './img';
import MenuIcon from './components/MenuIcon';
import { useCallback } from 'react';

interface MastheadAndHeaderProps {
    isSidebarOpen: boolean;
    setSidebarOpen: (isOpen: boolean) => void;
    handleOpenUserSettings: () => void;
    handleOpenHelpDialog: () => void;
}

const CustomMasthead = (props: MastheadAndHeaderProps) => {
    const { isSidebarOpen, setSidebarOpen } = props;

    const { t } = useTranslation();

    const handleSidebarToggle = useCallback(() => {
        setSidebarOpen(!isSidebarOpen);
    }, [isSidebarOpen, setSidebarOpen]);

    return (
        <Masthead style={{ justifyItems: "right", alignItems: 'center' }} className="mv-masthead">
            <MastheadMain>
                <MastheadBrand>
                    <Link to={routes.homePage.slug}>
                        <span className="masthead-logo unselectable" title={t('appTitle')}>
                            <img src={logo32w} alt="MVision logo" />
                        </span>
                    </Link>
                </MastheadBrand>
                <MastheadToggle>
                    <PageToggleButton variant="plain" aria-label="Side navigation toggle" isSidebarOpen={isSidebarOpen} onSidebarToggle={handleSidebarToggle}>
                        <MenuIcon iconUri={isSidebarOpen ? LeftPanelClose : RightPanelClose} height='25px' className="mv-masthead-toggle" />
                    </PageToggleButton>
                </MastheadToggle>
            </MastheadMain>
            <MastheadContent>
                <HeaderTools {...props} />
            </MastheadContent>
        </Masthead>
    );
};

const HeaderTools = (props: MastheadAndHeaderProps) => {
    const { t } = useTranslation();

    const targetList = useSelector(configurationTargetSelectors.selectConfigurationTargetList);
    const deploymentInfo = useSelector(appConfigSelectors.selectAppDeploymentInfo);
    const currentTarget = useSelector(configurationTargetSelectors.selectCurrent);
    const currentLangKey = getCurrentLangKey();
    // const isHelpDocumentAvailable = useSelector((state: StoreState) =>
    //     appConfigSelectors.selectIsHelpDocumentAvailable(state, currentLangKey)
    // );

    // Determine if the target selector should be shown
    const isTargetSelectorShown =
        targetList && deploymentInfo && deploymentInfo.deploymentMode === DeploymentMode.Cloud;

    // If in local mode, display local configuration message
    const hiddenTargetSelector = currentTarget && currentTarget.localMode ? (
        <div title={`${DEFAULT_APP_NAME} is using local configuration API.`}>Local mode</div>
    ) : null;

    return (
        <div className="mv-masthead-header-tools">
            {isTargetSelectorShown ? (
                <ConfigurationTargetSelector targetList={targetList} />
            ) : (
                hiddenTargetSelector
            )}
            <Button onClick={props.handleOpenHelpDialog} variant="plain" aria-label="Help" icon={<MenuIcon iconUri={HelpIcon} title={t('page.about.title')} height='32px' />} data-testid="about-dialog-button" />
            <Button variant="plain" onClick={props.handleOpenUserSettings} icon={<MenuIcon iconUri={TuneIcon} title={t('dialog.settings')} />} data-testid="settings-dialog-button" />
            <ProfileButton />
        </div>
    );
};

export default CustomMasthead;
